import React, { useState, useContext, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useMutation } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import languages, { getLanguageAcronym } from '../languages.js'
import { ADD_MEETING_WITH_CONTENT_MUTATION } from '../query/gqlQueries.js'
import { streamOllamaGenerate } from '../ollama.js'
import StudentLessonView from '../UI/StudentLessonView.js'
import styles from './AddProjectDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'

// Text-only models: these workbooks carry a real text layer, so no OCR / vision
// model is needed. Per-section chunking keeps every call small enough that even
// the 14B/7B models are reliable and never approach the Mac Studio's RAM ceiling.
const MODELS = ['qwen2.5:14b', 'gemma3:27b', 'qwen2.5:7b']
const LEVELS = ['A0', 'A0–A1', 'A1', 'A2', 'B1']

// ── file → text ──────────────────────────────────────────────────────────────
// Selınay's workbooks are HTML-exported PDFs with a real text layer, so we can
// pull the text directly (no OCR / vision model). PDFs go through pdf.js; .html
// sources (if kept) are even cleaner; .txt just reads through.
const readAsText = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(String(r.result || '')); r.onerror = rej; r.readAsText(file)
})
const stripHtml = (html) => {
  try { const doc = new DOMParser().parseFromString(html, 'text/html'); return (doc.body?.innerText || doc.body?.textContent || '').trim() }
  catch { return String(html).replace(/<[^>]+>/g, ' ') }
}
// pdf.js is loaded lazily from a CDN only when a PDF is actually dropped, so it
// never enters the bundle — nothing to npm install, and the build stays clean.
const PDFJS_VER = '3.11.174'
let pdfjsPromise = null
const loadPdfJs = () => {
  if (typeof window !== 'undefined' && window.pdfjsLib) return Promise.resolve(window.pdfjsLib)
  if (pdfjsPromise) return pdfjsPromise
  pdfjsPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = `https://unpkg.com/pdfjs-dist@${PDFJS_VER}/legacy/build/pdf.min.js`
    s.onload = () => {
      const lib = window.pdfjsLib
      if (!lib) { pdfjsPromise = null; return reject(new Error('PDF reader failed to load.')) }
      try { lib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS_VER}/legacy/build/pdf.worker.min.js` } catch { /* noop */ }
      resolve(lib)
    }
    s.onerror = () => { pdfjsPromise = null; reject(new Error('Could not load the PDF reader (it needs an internet connection). Paste the text or drop an HTML file instead.')) }
    document.body.appendChild(s)
  })
  return pdfjsPromise
}
const extractPdfText = async (file) => {
  const pdfjs = await loadPdfJs()
  const data = await file.arrayBuffer()
  const pdf = await pdfjs.getDocument({ data }).promise
  let out = ''
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p)
    const tc = await page.getTextContent()
    out += tc.items.map((i) => i.str).join(' ') + '\n\n'
  }
  return out.trim()
}
const detectLevel = (t) => {
  if (/A0\s*[–—-]\s*A1/i.test(t)) return 'A0–A1'
  if (/\bA1\b/.test(t) && !/\bA0\b/.test(t)) return 'A1'
  if (/\bA0\b/.test(t)) return 'A0'
  if (/\bA2\b/.test(t)) return 'A2'
  if (/\bB1\b/.test(t)) return 'B1'
  return null
}

function parseLooseJson(raw) {
  let s = String(raw || '').replace(/```json/gi, '').replace(/```/g, '').trim()
  try { return JSON.parse(s) } catch { /* salvage */ }
  const a = s.indexOf('{'); const b = s.lastIndexOf('}')
  if (a >= 0 && b > a) { try { return JSON.parse(s.slice(a, b + 1)) } catch { /* give up */ } }
  return null
}

// Selınay's workbooks share one skeleton: numbered teaching sections, Practice
// A–F blocks, a recycling reading with questions, a mini dialogue, and an answer
// key. We ask the model to emit exactly that tree.
const buildPrompt = (text, languageName, level) => `You are structuring a ${languageName} lesson workbook (level ${level}) written by a teacher, into JSON a practice app can render. The workbook text is delimited below.

Break it into its natural SECTIONS in reading order. Recognise these recurring parts:
- teaching/concept blocks (a short rule + examples)
- vocabulary lists (word → English)
- paradigm/pattern TABLES (person × form, owner × noun, tense grids, PATTERN|MEANING|EXAMPLE)
- Practice A/B/C… exercises (each has a TYPE)
- a Reading passage with comprehension questions
- a mini dialogue
- speaking-practice questions
There is usually an ANSWER KEY at the end — USE IT to fill every exercise/question "a" (answer). Never invent answers that contradict the key.

For each Practice exercise, set "type" to one of: match, order, fill, choose, transform, translate, speak. Put each question as an item {q, a, options?}. For "choose" include the multiple-choice "options". Keep at most 6 items per exercise. Keep "q" short.

Return ONLY minified JSON (no markdown):
{
 "title": "", "level": "${level}", "goal": "",
 "sections": [
   { "kind": "concept|vocab|paradigm|patterns|practice", "title": "",
     "note": "", "words": [{"tr":"","en":""}], "sentences": [{"tr":"","en":""}],
     "table": { "columns": [], "rows": [[]] },
     "exercise": { "type": "", "prompt": "", "items": [{"q":"","a":"","options":[]}] } }
 ],
 "reading": { "title": "", "text": "", "glossary": [{"tr":"","en":""}], "questions": [{"q":"","a":""}] },
 "dialogue": { "title": "", "lines": [{"who":"","tr":""}] },
 "speaking": [""]
}
Omit "table", "exercise", "reading", "dialogue", "speaking" when a lesson has none. Every "tr" MUST be in ${languageName}; every "en"/"a"-translation in English.

WORKBOOK TEXT:
"""
${text.trim().slice(0, 14000)}
"""`

const cleanLesson = (obj, langCode, languageName, level) => {
  if (!obj || typeof obj !== 'object') return null
  const S = (v) => String(v || '').trim()
  const pairs = (arr, ka, kb) => (Array.isArray(arr) ? arr : []).map(x => ({ tr: S(x?.[ka]), en: S(x?.[kb]) })).filter(p => p.tr)
  const sections = (Array.isArray(obj.sections) ? obj.sections : []).map(sec => {
    const ex = sec?.exercise
    const items = Array.isArray(ex?.items) ? ex.items.map(it => ({ q: S(it?.q), a: S(it?.a), options: Array.isArray(it?.options) ? it.options.map(S).filter(Boolean) : undefined })).filter(it => it.q || it.a) : []
    const tbl = sec?.table
    const table = Array.isArray(tbl?.rows) && tbl.rows.length ? { columns: (tbl.columns || []).map(S), rows: tbl.rows.map(r => (Array.isArray(r) ? r.map(S) : [])) } : undefined
    return {
      kind: ['concept', 'vocab', 'paradigm', 'patterns', 'practice'].includes(sec?.kind) ? sec.kind : (items.length ? 'practice' : 'concept'),
      title: S(sec?.title) || 'Section',
      note: S(sec?.note) || undefined,
      words: pairs(sec?.words, 'tr', 'en'),
      sentences: pairs(sec?.sentences, 'tr', 'en'),
      table,
      exercise: items.length ? { type: S(ex?.type) || 'translate', prompt: S(ex?.prompt), items } : undefined,
    }
  }).filter(s => s.words.length || s.sentences.length || s.exercise || s.table || s.note)

  const r = obj.reading
  const reading = S(r?.text) ? { title: S(r?.title) || 'Reading', text: S(r.text), glossary: pairs(r?.glossary, 'tr', 'en'), questions: (Array.isArray(r?.questions) ? r.questions : []).map(q => ({ q: S(q?.q), a: S(q?.a) })).filter(q => q.q) } : undefined
  const d = obj.dialogue
  const lines = (Array.isArray(d?.lines) ? d.lines : []).map(l => ({ who: S(l?.who), tr: S(l?.tr) })).filter(l => l.tr)
  const dialogue = lines.length ? { title: S(d?.title) || 'Mini dialogue', lines } : undefined
  const speaking = (Array.isArray(obj.speaking) ? obj.speaking : []).map(S).filter(Boolean)

  return { title: S(obj.title) || 'Imported lesson', level: S(obj.level) || level, goal: S(obj.goal), language: langCode, languageName, sections, reading, dialogue, speaking }
}

export default function ImportLessonDialog() {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)

  const [text, setText] = useState('')
  const [title, setTitle] = useState('')
  const [level, setLevel] = useState('A0')
  const [languageName, setLanguageName] = useState('Turkish')
  const [model, setModel] = useState('qwen2.5:14b')
  const [lessonDate, setLessonDate] = useState(new Date().toISOString().split('T')[0])
  const [status, setStatus] = useState('idle') // idle | analysing | reviewing | saving | completed | error
  const [errorMessage, setErrorMessage] = useState('')
  const [progressMsg, setProgressMsg] = useState('')
  const [lesson, setLesson] = useState(null)
  const [previewing, setPreviewing] = useState(false)
  const [fileName, setFileName] = useState('')
  const [importingFile, setImportingFile] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileRef = useRef(null)

  const [addMeetingWithContent] = useMutation(ADD_MEETING_WITH_CONTENT_MUTATION)
  const isOpen = dialogStates['dialogImportLesson']

  const handleClose = () => {
    closeDialog('dialogImportLesson')
    setText(''); setTitle(''); setStatus('idle'); setErrorMessage(''); setProgressMsg(''); setLesson(null); setPreviewing(false)
    setLessonDate(new Date().toISOString().split('T')[0])
  }

  const langCode = getLanguageAcronym(languageName) || 'tur'

  const handleFile = async (file) => {
    if (!file) return
    setFileName(file.name); setImportingFile(true); setErrorMessage('')
    try {
      const name = file.name.toLowerCase()
      let txt = ''
      if (name.endsWith('.pdf')) txt = await extractPdfText(file)
      else if (name.endsWith('.html') || name.endsWith('.htm')) txt = stripHtml(await readAsText(file))
      else txt = await readAsText(file)
      if (!txt.trim()) throw new Error('No selectable text found — this looks like a scanned (image-only) PDF. Paste the text instead.')
      setText(txt)
      const lv = detectLevel(txt); if (lv) setLevel(lv)
    } catch (e) {
      setErrorMessage(e.message || 'Could not read that file.')
      setFileName('')
    }
    setImportingFile(false)
  }

  const handleAnalyse = async () => {
    if (!text.trim()) return
    setStatus('analysing'); setErrorMessage(''); setProgressMsg('Reading the workbook…')
    const serverUrl = process.env.REACT_APP_OLLAMA_URI || 'http://localhost:11434'
    try {
      const raw = await streamOllamaGenerate({
        serverUrl, apiKey: process.env.REACT_APP_OLLAMA_KEY,
        body: { model, prompt: buildPrompt(text, languageName, level), format: 'json', options: { temperature: 0.2, num_predict: 4200 } },
        onProgress: (acc) => setProgressMsg(`Structuring the lesson… (${acc.length} chars)`),
      })
      const parsed = cleanLesson(parseLooseJson(raw), langCode, languageName, level)
      if (!parsed || parsed.sections.length === 0) { setStatus('error'); setErrorMessage('Could not structure this text — try a smaller paste, a different model, or check the text has the lesson content.'); return }
      if (title.trim()) parsed.title = title.trim()
      setLesson(parsed); setStatus('reviewing')
    } catch (err) {
      setStatus('error')
      setErrorMessage((err.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed — check the tunnel / Ollama.' : (err.message || 'Analysis failed.'))
    }
  }

  const handleSave = async () => {
    if (!lesson) return
    setStatus('saving')
    // Flatten every section's words & sentences (+ reading glossary) so the
    // existing flashcard / recall / scramble modes work on this lesson too. The
    // full sectioned tree + exercises + answer keys ride along in lessonData.
    const words = []
    const sentences = []
    lesson.sections.forEach(s => {
      (s.words || []).forEach(w => words.push({ word: w.tr, translation: w.en, lang: langCode }))
      ;(s.sentences || []).forEach(se => sentences.push({ sourceText: se.tr, targetText: se.en, sourceLang: langCode, targetLang: 'eng' }))
    })
    ;(lesson.reading?.glossary || []).forEach(g => words.push({ word: g.tr, translation: g.en, lang: langCode }))
    const tasks = []
    if (lesson.speaking?.length) tasks.push({ title: `${lesson.title} — speaking`, type: 'Homework', description: lesson.speaking.slice(0, 6).join(' • '), priority: 'Medium' })

    try {
      await addMeetingWithContent({
        variables: {
          input: {
            meeting: {
              title: lesson.title, type: 'Language Class',
              description: `${lesson.level} · ${lesson.goal || 'Imported workbook'}`.slice(0, 240),
              meetingDate: lessonDate || null, username,
              lessonData: JSON.stringify(lesson),
            },
            sentences, words, tasks,
          },
        },
      })
      setStatus('completed')
    } catch (err) {
      setStatus('error'); setErrorMessage(err.message)
    }
  }

  if (!isOpen) return null

  if (previewing && lesson) {
    return ReactDOM.createPortal(
      <StudentLessonView lesson={lesson} onClose={() => setPreviewing(false)} />, document.body
    )
  }

  const sectionCount = lesson?.sections.length || 0
  const exerciseCount = lesson?.sections.filter(s => s.exercise).length || 0

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '760px' }}>
        <button className={styles.closeBtn} onClick={handleClose}><CloseIcon /></button>

        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Import Lesson</div>
            <h2 className={styles.title}>Turn a workbook into a class</h2>
          </div>
        </div>

        <div className={styles.container}>
          {status === 'completed' ? (
            <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
              <div style={{ fontSize: '2.4rem' }}>🎓</div>
              <h3 style={{ margin: '0.6rem 0' }}>“{lesson?.title}” saved as a Language Class</h3>
              <p style={{ color: '#8a8a95' }}>{sectionCount} sections · {exerciseCount} exercises{lesson?.reading ? ' · reading' : ''}{lesson?.dialogue ? ' · dialogue' : ''}</p>
              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center', marginTop: '1rem' }}>
                <button className={styles.input} style={{ cursor: 'pointer', width: 'auto', padding: '0.6rem 1.2rem' }} onClick={() => setPreviewing(true)}>▶ Open as student</button>
                <button className={styles.input} style={{ cursor: 'pointer', width: 'auto', padding: '0.6rem 1.2rem' }} onClick={handleClose}>Close</button>
              </div>
            </div>
          ) : status === 'analysing' || status === 'saving' ? (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div className="spinner" style={{ width: 34, height: 34, border: '3px solid #2a2a33', borderTopColor: '#34d399', borderRadius: '50%', margin: '0 auto', animation: 'spin 0.8s linear infinite' }} />
              <p style={{ color: '#8a8a95', marginTop: '1rem' }}>{status === 'saving' ? 'Saving the class…' : progressMsg}</p>
              <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
            </div>
          ) : status === 'reviewing' && lesson ? (
            <>
              <div style={{ background: '#141418', border: '1px solid #2a2a33', borderRadius: 12, padding: '1rem', maxHeight: 360, overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <span style={{ background: 'rgba(52,211,153,0.14)', color: '#6ee7b7', borderRadius: 999, padding: '0.1rem 0.6rem', fontSize: '0.75rem', fontWeight: 600 }}>{lesson.level}</span>
                  <h3 style={{ margin: 0 }}>{lesson.title}</h3>
                </div>
                {lesson.goal && <p style={{ color: '#8a8a95', fontSize: '0.9rem', margin: '0.4rem 0 0.8rem' }}>{lesson.goal}</p>}
                <ol style={{ paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {lesson.sections.map((s, i) => (
                    <li key={i} style={{ fontSize: '0.9rem' }}>
                      <b>{s.title}</b> <span style={{ color: '#6a6a75' }}>· {s.kind}</span>
                      <span style={{ color: '#8a8a95' }}>{s.words?.length ? ` · ${s.words.length} words` : ''}{s.sentences?.length ? ` · ${s.sentences.length} sentences` : ''}{s.exercise ? ` · ✍️ ${s.exercise.type} (${s.exercise.items.length})` : ''}{s.table ? ' · 📐 table' : ''}</span>
                    </li>
                  ))}
                </ol>
                <div style={{ color: '#8a8a95', fontSize: '0.85rem', marginTop: '0.7rem', display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
                  {lesson.reading && <span>📖 Reading ({lesson.reading.questions?.length || 0} Qs)</span>}
                  {lesson.dialogue && <span>💬 Dialogue ({lesson.dialogue.lines.length})</span>}
                  {lesson.speaking?.length > 0 && <span>🗣️ Speaking ({lesson.speaking.length})</span>}
                </div>
              </div>
              {errorMessage && <div style={{ color: '#f87171', marginTop: '0.6rem' }}>{errorMessage}</div>}
              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                <button className={styles.input} style={{ cursor: 'pointer', width: 'auto', padding: '0.6rem 1.2rem' }} onClick={() => setPreviewing(true)}>▶ Preview as student</button>
                <button className={styles.input} style={{ cursor: 'pointer', width: 'auto', padding: '0.6rem 1.2rem' }} onClick={() => { setStatus('idle') }}>← Edit input</button>
                <button className={styles.input} style={{ cursor: 'pointer', width: 'auto', padding: '0.6rem 1.4rem', background: '#34d399', color: '#06281d', fontWeight: 600, border: 'none' }} onClick={handleSave}>Save class →</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div className={styles.field} style={{ flex: 2, minWidth: 200 }}>
                  <div className={styles.fieldLabel}>Lesson title (optional — auto-detected)</div>
                  <input className={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Bu ne? Bu kim?" />
                </div>
                <div className={styles.field} style={{ flex: 1, minWidth: 110 }}>
                  <div className={styles.fieldLabel}>Level</div>
                  <select className={styles.input} value={level} onChange={(e) => setLevel(e.target.value)}>{LEVELS.map(l => <option key={l} value={l}>{l}</option>)}</select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div className={styles.field} style={{ flex: 1, minWidth: 140 }}>
                  <div className={styles.fieldLabel}>Language</div>
                  <select className={styles.input} value={languageName} onChange={(e) => setLanguageName(e.target.value)}>{Object.values(languages).map(v => <option key={v} value={v}>{v}</option>)}</select>
                </div>
                <div className={styles.field} style={{ flex: 1, minWidth: 140 }}>
                  <div className={styles.fieldLabel}>Model</div>
                  <select className={styles.input} value={model} onChange={(e) => setModel(e.target.value)}>{MODELS.map(m => <option key={m} value={m}>{m}</option>)}</select>
                </div>
                <div className={styles.field} style={{ flex: 1, minWidth: 140 }}>
                  <div className={styles.fieldLabel}>Date</div>
                  <input type="date" className={styles.input} value={lessonDate} onChange={(e) => setLessonDate(e.target.value)} />
                </div>
              </div>
              <div className={styles.field}>
                <div className={styles.fieldLabel}>Add Selınay's workbook — PDF, HTML or TXT</div>
                <div
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]) }}
                  style={{ border: `1.5px dashed ${dragOver ? '#34d399' : '#3a3a42'}`, borderRadius: 10, padding: '1.1rem', textAlign: 'center', cursor: 'pointer', color: dragOver ? '#6ee7b7' : '#8a8a95', background: dragOver ? 'rgba(52,211,153,0.06)' : '#0d0d0f', transition: 'all .12s', fontSize: '0.92rem' }}>
                  {importingFile ? '⏳ Reading the file…' : fileName ? `📄 ${fileName} — loaded below, edit if needed` : '📎 Drop a workbook PDF here (or click to choose) — HTML / TXT also work'}
                </div>
                <input ref={fileRef} type="file" accept=".pdf,.html,.htm,.txt,.md" style={{ display: 'none' }} onChange={(e) => handleFile(e.target.files?.[0])} />
              </div>
              <div className={styles.field}>
                <div className={styles.fieldLabel}>…or paste the workbook text (include the Practice blocks + ANSWER KEY)</div>
                <textarea value={text} onChange={(e) => setText(e.target.value)} spellCheck="false"
                  placeholder={'Paste the full lesson text here — including the Practice blocks and the ANSWER KEY so exercises can be auto-graded.'}
                  style={{ width: '100%', minHeight: 220, resize: 'vertical', background: '#0d0d0f', border: '1px solid #3a3a42', borderRadius: 8, color: '#e8e8ec', padding: '0.7rem', fontFamily: 'inherit', fontSize: '0.92rem' }} />
              </div>
              {errorMessage && <div style={{ color: '#f87171' }}>{errorMessage}</div>}
              <button className={styles.input} style={{ cursor: 'pointer', width: '100%', padding: '0.75rem', background: '#34d399', color: '#06281d', fontWeight: 600, border: 'none', marginTop: '0.4rem' }} onClick={handleAnalyse} disabled={!text.trim()}>✦ Analyse & structure</button>
            </>
          )}
        </div>
      </div>
    </div>, document.body
  )
}
